import React from 'react';
import { StateSetterFunction } from '@repo/ui/src/types/helpers';
import useEventListener from '@repo/ui/src/hooks/useEventListener';
import noop from 'lodash/noop';

type Func<T> = () => T;

const isFunc = <T>(val: T | Func<T>): val is Func<T> => typeof val === 'function';

const resolveValue = <T>(value: Func<T> | T) => (isFunc(value) ? value() : value);

type GetInitialValueFuncType<T> = () => T;

interface Config {
  version?: number;
  ignoreVersion?: boolean;
  useSessionStorage?: boolean;
}

const useStorage = (useSessionStorage = false) => {
  const [cookiesEnabled, setCookiesEnabled] = React.useState(false);

  React.useEffect(() => {
    try {
      // try accessing a property on localStorage
       
      window.localStorage.length;
      setCookiesEnabled(true);
    } catch {
      // do nothing, cookies are not enabled
    }
  }, []);

  return React.useMemo(() => {
    if (cookiesEnabled) {
      return window[useSessionStorage ? 'sessionStorage' : 'localStorage'];
    }
    return {
      setItem: noop,
      getItem: noop,
    };
  }, [useSessionStorage, cookiesEnabled]);
};

export const useLocalStorage = <T>(
  key: string,
  initialValue: T | GetInitialValueFuncType<T>,
  { version = 1, ignoreVersion = false, useSessionStorage = false }: Config = {},
): [T, StateSetterFunction<T>, boolean] => {
  const [storedValue, setStoredValue] = React.useState(initialValue);
  const [isLoading, setLoading] = React.useState(true);
  const storage = useStorage(useSessionStorage);
  const getInitialValue = React.useCallback(() => resolveValue(initialValue), [initialValue]);

  const loadValue = React.useCallback(() => {
    try {
      const item = storage.getItem(key);
      if (item == null) {
        return getInitialValue();
      }
      const { value, version: storedVersion } = JSON.parse(item || '{}');
      if (!ignoreVersion && storedVersion !== version) {
        return getInitialValue();
      }
      return value;
    } catch (e) {
      console.error(e);
    }
    return getInitialValue();
  }, [key, storage, version, getInitialValue, ignoreVersion]);

  const setState = React.useCallback(
    (newValue: any) => {
      const resolvedValue = typeof newValue === 'function' ? newValue(loadValue()) : newValue;
      try {
        storage.setItem(key, JSON.stringify({ value: resolvedValue, version }));

        // Dispatch a custom event so every useLocalStorage hook is notified
        window.dispatchEvent(
          new CustomEvent('local-storage', {
            detail: {
              key,
            },
          }),
        );
      } catch (e) {
        console.error(e);
      }
      setStoredValue(resolvedValue);
    },
    [key, version, storage, loadValue],
  );

  React.useEffect(() => {
    if (storage instanceof Storage) {
      setStoredValue(loadValue());
      setLoading(false);
    }
  }, [loadValue, storage]);

  const handleStorageChange = React.useCallback(
    (event: StorageEvent | CustomEvent) => {
      // exclude changes for other keys
      if (
        (event instanceof StorageEvent && event.key !== key) ||
        (event instanceof CustomEvent && event.detail.key !== key)
      ) {
        return;
      }
      setStoredValue(loadValue());
    },
    [key, loadValue],
  );

  // this only works for other documents, not the current one
  useEventListener('storage', handleStorageChange);

  // this is a custom event, triggered in writeValueToLocalStorage
  useEventListener('local-storage', handleStorageChange);

  return [storedValue, setState, isLoading];
};
